// The persistent lever strip + the live re-rank wiring. The sliders are the only control
// surface in v0.1 (single role, build-plan Decision #7). Moving one re-ranks every visible
// BNA instantly in the browser and persists the shared position to the Worker (debounced).
import { getLevers, setPosition, rankedForUnit } from './store';

const escapeHtml = (s: string): string =>
  s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
  );

/** Where a slider leans, for the readout under each lever. */
function leanLabel(position: number, leftPole: string, rightPole: string): string {
  if (position === 50) return 'neutral';
  return position > 50 ? `leans ${rightPole}` : `leans ${leftPole}`;
}

export function renderLeverStrip(): string {
  const levers = getLevers();
  if (levers.length === 0) return '';
  const rows = levers
    .map(
      (l) => `
      <div class="lever">
        <div class="lever__poles">
          <span class="lever__pole">${escapeHtml(l.leftPoleLabel)}</span>
          <span class="lever__name">${escapeHtml(l.name)}</span>
          <span class="lever__pole lever__pole--right">${escapeHtml(l.rightPoleLabel)}</span>
        </div>
        <input
          type="range" min="0" max="100" step="1" value="${l.position}"
          class="lever__slider" id="lever-${l.id}" data-lever="${l.id}"
          aria-label="${escapeHtml(l.name)}: ${escapeHtml(l.leftPoleLabel)} to ${escapeHtml(l.rightPoleLabel)}" />
        <div class="lever__readout" id="readout-${l.id}">
          ${l.position} · ${leanLabel(l.position, l.leftPoleLabel, l.rightPoleLabel)}
        </div>
      </div>`,
    )
    .join('');
  return `
    <section class="lever-strip" aria-label="Strategic posture">
      <div class="lever-strip__head">Strategic posture</div>
      <div class="levers">${rows}</div>
    </section>`;
}

function tier(priority: number): 'high' | 'mid' | 'low' {
  if (priority >= 70) return 'high';
  if (priority >= 45) return 'mid';
  return 'low';
}

/** Ranked BNA rows for one unit. `limit` caps the count (used by the compact command-center tiles). */
export function renderBnaItems(unitId: string, limit?: number): string {
  let ranked = rankedForUnit(unitId);
  if (limit !== undefined) ranked = ranked.slice(0, limit);
  if (ranked.length === 0) return `<div class="bna-empty">No active actions.</div>`;
  return ranked
    .map(({ bna, priority }, i) => {
      const p = Math.round(priority);
      return `
      <div class="bna" data-bna="${bna.id}">
        <div class="bna__rank">${i + 1}</div>
        <div class="bna__body">
          <div class="bna__title">${escapeHtml(bna.title)}</div>
          ${bna.description ? `<div class="bna__desc">${escapeHtml(bna.description)}</div>` : ''}
        </div>
        <div class="bna__priority bna__priority--${tier(p)}">
          <span class="bna__priority-num">${p}</span>
          <span class="bna__priority-label">priority</span>
        </div>
      </div>`;
    })
    .join('');
}

/**
 * A ranked-list container. It carries data-unit / data-limit so refreshRankings() can rewrite
 * just its contents on every slider move without touching the sliders themselves.
 */
export function renderBnaList(unitId: string, limit?: number): string {
  const limitAttr = limit !== undefined ? ` data-limit="${limit}"` : '';
  return `<div class="bna-list" data-unit="${unitId}"${limitAttr}>${renderBnaItems(unitId, limit)}</div>`;
}

let rafHandle = 0;
/** Rewrite every ranked-list on the page from the current posture. Throttled to one per frame. */
export function refreshRankings(): void {
  if (rafHandle) return;
  rafHandle = requestAnimationFrame(() => {
    rafHandle = 0;
    document.querySelectorAll<HTMLElement>('.bna-list[data-unit]').forEach((el) => {
      const unitId = el.dataset.unit;
      if (!unitId) return;
      const limitRaw = el.dataset.limit;
      const limit = limitRaw !== undefined ? Number(limitRaw) : undefined;
      el.innerHTML = renderBnaItems(unitId, limit);
    });
  });
}

/** Attach slider listeners after a render. Input → instant local re-rank + debounced persist. */
export function wireLeverStrip(): void {
  document.querySelectorAll<HTMLInputElement>('.lever__slider[data-lever]').forEach((slider) => {
    const leverId = slider.dataset.lever;
    if (!leverId) return;
    slider.addEventListener('input', () => {
      const position = Number(slider.value);
      setPosition(leverId, position);
      const lever = getLevers().find((l) => l.id === leverId);
      const readout = document.getElementById(`readout-${leverId}`);
      if (lever && readout) {
        readout.textContent = `${position} · ${leanLabel(position, lever.leftPoleLabel, lever.rightPoleLabel)}`;
      }
      refreshRankings();
    });
  });
}
